import { type FormEvent, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useClientAuth } from "../lib/auth-context";
import { ClientLink, useClientRouter } from "../lib/router-context";

type LinkState = "checking" | "valid" | "invalid" | "error";

function passwordError(password: string): string | null {
  if (password.length < 10) return "Le mot de passe doit contenir au moins 10 caractères.";
  if (!/\d/.test(password)) return "Le mot de passe doit contenir au moins 1 chiffre.";
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password)) {
    return "Le mot de passe doit contenir au moins 1 caractère spécial.";
  }
  return null;
}

export default function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [linkState, setLinkState] = useState<LinkState>("checking");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { logout } = useClientAuth();
  const { navigate } = useClientRouter();

  useEffect(() => {
    if (!token) {
      setLinkState("invalid");
      return;
    }

    const controller = new AbortController();
    fetch(`/api/espace-client/auth/link?token=${encodeURIComponent(token)}&type=reset`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async response => {
        if (!response.ok) throw new Error("verification-failed");
        const data = await response.json();
        setLinkState(data.valid === true ? "valid" : "invalid");
      })
      .catch((requestError: unknown) => {
        if (requestError instanceof DOMException && requestError.name === "AbortError") return;
        setLinkState("error");
      });

    return () => controller.abort();
  }, [token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const strengthError = passwordError(password);
    if (strengthError) { setError(strengthError); return; }
    if (password !== confirmation) { setError("Les mots de passe ne correspondent pas."); return; }
    if (!token) { setLinkState("invalid"); return; }

    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch("/api/espace-client/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (!response.ok) {
        if ([400, 401, 403, 404, 410].includes(response.status)) {
          setLinkState("invalid");
          return;
        }
        setError("La réinitialisation n’a pas pu aboutir. Veuillez réessayer.");
        return;
      }
      logout();
      setSuccess(true);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold">Réinitialiser mon mot de passe</h1>
          <p className="text-sm text-white/75 mt-2">Choisissez un nouveau mot de passe personnel.</p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl shadow-2xl p-6">
          {linkState === "checking" && (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
              <Loader2 className="w-4 h-4 animate-spin" /> Vérification du lien…
            </p>
          )}

          {linkState === "error" && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-destructive" role="alert">Impossible de vérifier le lien pour le moment. Veuillez réessayer.</p>
              <button type="button" onClick={() => window.location.reload()} className="text-sm text-primary hover:underline">
                Réessayer
              </button>
            </div>
          )}

          {linkState === "invalid" && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center"><AlertCircle className="w-8 h-8 text-destructive" /></div>
              <h2 className="font-semibold">Ce lien n’est plus valide</h2>
              <p className="text-sm text-muted-foreground">Il a peut-être expiré ou a déjà été utilisé.</p>
              <ClientLink to="/mot-de-passe-oublie" className="inline-block text-sm text-primary hover:underline">
                Demander un nouveau lien
              </ClientLink>
            </div>
          )}

          {linkState === "valid" && !success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="reset-password" className="text-sm font-medium">Nouveau mot de passe</label>
                <input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  required
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  className="w-full h-11 mt-1.5 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>
              <div>
                <label htmlFor="reset-confirmation" className="text-sm font-medium">Confirmer le mot de passe</label>
                <input
                  id="reset-confirmation"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  required
                  value={confirmation}
                  onChange={event => setConfirmation(event.target.value)}
                  className="w-full h-11 mt-1.5 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">10 caractères minimum, avec au moins un chiffre et un caractère spécial.</p>
              {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Réinitialiser le mot de passe
              </button>
            </form>
          )}

          {success && (
            <div className="space-y-4 text-center" role="status">
              <div className="flex justify-center"><CheckCircle2 className="w-8 h-8 text-primary" /></div>
              <h2 className="font-semibold">Mot de passe mis à jour</h2>
              <p className="text-sm text-muted-foreground">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
              <button type="button" onClick={() => navigate("/connexion")} className="text-sm text-primary hover:underline">
                Retour à la connexion
              </button>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-white/70">Espace Client Jobotto</p>
      </div>
    </div>
  );
}